import React, { useState, useEffect } from 'react';
import './App.scss';
import NewsUpdate from './components/NewsUpdate/NewsUpdate.jsx';
import DataManagement from './components/DataManagement/DataManagement.jsx';
import Auth from './components/Auth/Auth.jsx';
import useDataManagement from './hooks/useDataManagement.js';
import { API_NAMES, COUNTRY_CODES, LANGUAGE_CODES, ROWS_PER_PAGE } from './constants.js';
import { isAuthenticated, removeAuthToken } from './utils/auth.js';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [apiName, setApiName] = useState(API_NAMES.HOME);
  const [countryCode, setCountryCode] = useState(COUNTRY_CODES.NZ);
  const [languageCode, setLanguageCode] = useState(LANGUAGE_CODES.EN);
  const [locationId, setLocationId] = useState(0);
  const [maxNumberOfRows, setMaxNumberOfRows] = useState(ROWS_PER_PAGE.FIFTY);
  const [locations, setLocations] = useState([]);
  const [selectedNewsData, setSelectedNewsData] = useState(null);

  const {
    rows,
    count,
    currentPage,
    setCurrentPage,
    setCount,
    getData,
    handleSelect,
    handleUpdate,
    handleShare,
    handleDelete,
  } = useDataManagement();

  // 인증 상태 확인
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setAuthenticated(authStatus);
    };
    
    checkAuth();
    
    // 주기적으로 인증 상태 확인 (5분마다)
    const authInterval = setInterval(checkAuth, 5 * 60 * 1000);
    
    return () => clearInterval(authInterval);
  }, []);

  // 로그아웃 핸들러
  const handleLogout = () => {
    removeAuthToken();
    setAuthenticated(false);
  };

  // 인증 성공 핸들러
  const handleAuthenticated = () => {
    setAuthenticated(true);
  };

  useEffect(() => {
    if (countryCode) {
      import(`./json/${countryCode.toLowerCase()}-location.json`)
        .then(module => {
          const locationData = module.default.map(location => ({
            ...location,
            countryCode: countryCode
          }));
          setLocations(locationData);
        })
        .catch(error => {
          console.error('Error loading locations:', error);
          setLocations([]);
        });
    } else {
      setLocations([]);
    }
  }, [countryCode]);

  useEffect(() => {
    if (count === 0) {
      getData(apiName, countryCode, languageCode, locationId, maxNumberOfRows);
    }
  }, [count, getData, apiName, countryCode, languageCode, locationId, maxNumberOfRows]);

  const handleApiNameChange = (e) => {
    const selectedApi = e.target.value;
    setApiName(selectedApi);
    setCurrentPage(1);
    setCount(0);

    // youtube-api 선택 시 한국어로, 그 외에는 영어로 설정
    if (selectedApi === API_NAMES.YOUTUBE) {
      setLanguageCode(LANGUAGE_CODES.KO);
    } else {
      setLanguageCode(LANGUAGE_CODES.EN);
    }
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
    setCurrentPage(1);
    setCount(0);
  };

  const handleLanguageCodeChange = (e) => {
    setLanguageCode(e.target.value);
    setCurrentPage(1);
    setCount(0);
  };

  const handleLocationIdChange = (e) => {
    setLocationId(e.target.value);
    setCurrentPage(1);
    setCount(0);
  };

  const handleMaxRowsChange = (e) => {
    setMaxNumberOfRows(e.target.value);
    setCurrentPage(1);
    setCount(0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCount(0);
  };

  const handleNewsSelect = (selectedData) => {
    console.log('News Selected:', selectedData);
    setSelectedNewsData(selectedData);
  };

  const handleNewsUpdateSelect = (newsUpdate) => {
    setSelectedNewsData(newsUpdate);
  };

  // 인증되지 않은 경우 로그인 페이지 표시
  if (!authenticated) {
    return <Auth onAuthenticated={handleAuthenticated} />;
  }

  if (count === 0) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-content">
            <div className="country-selector">
              <select value={countryCode} onChange={handleCountryCodeChange}>
                {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                  <option key={key} value={value}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
            <button className="logout-button" onClick={handleLogout}>
              로그아웃
            </button>
          </div>
        </header>
        <div className="loading">데이터를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="country-selector">
            <select value={countryCode} onChange={handleCountryCodeChange}>
              {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                <option key={key} value={value}>
                  {key}
                </option>
              ))}
            </select>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </header>
      <main>
        <div className="content-area">
          <NewsUpdate
            onShareClicked={handleShare}
            selectedData={selectedNewsData}
            languageCode={languageCode}
            countryCode={countryCode}
            locations={locations}
            locationId={locationId}
          />
          <DataManagement
            apiName={apiName}
            countryCode={countryCode}
            languageCode={languageCode}
            locationId={locationId}
            maxNumberOfRows={maxNumberOfRows}
            currentPage={currentPage}
            count={count}
            rows={rows}
            locations={locations}
            onApiNameChange={handleApiNameChange}
            onCountryCodeChange={handleCountryCodeChange}
            onLanguageCodeChange={handleLanguageCodeChange}
            onLocationIdChange={handleLocationIdChange}
            onMaxRowsChange={handleMaxRowsChange}
            onPageChange={handlePageChange}
            onSelectClicked={handleNewsSelect}
            onUpdateClicked={(id, title, title_ko) => handleUpdate(apiName, countryCode, id, title, title_ko)}
            onShareClicked={handleShare}
            onDeleteClicked={(id) => handleDelete(apiName, countryCode, id)}
            onNewsUpdateSelect={handleNewsUpdateSelect}
          />
        </div>
      </main>
    </div>
  );
}

export default App;